import { useEffect, useMemo, useRef, useState } from "react";
import {
  MDBBtn,
  MDBIcon,
  MDBModal,
  MDBModalBody,
  MDBModalFooter,
  MDBModalHeader,
  MDBTable,
} from "mdbreact";
import { REASONS } from "./common";

/**
 * Dispenses available stock as a monthly expense: pick the items from the
 * inventory (picked ones are not offered again), their quantity, and the reason.
 */
export default function DispenseModal({
  show,
  rows = [],
  // item already picked when opened from its row
  initialKey = "",
  toggle,
  onSubmit,
  isLoading,
}) {
  const [lines, setLines] = useState([]), // [{ key, qty }]
    [search, setSearch] = useState(""),
    [isOpen, setIsOpen] = useState(false),
    [cursor, setCursor] = useState(0),
    [reason, setReason] = useState("correction"),
    [remarks, setRemarks] = useState(""),
    searchRef = useRef(null);

  useEffect(() => {
    if (!show) return;
    const picked = rows.find((row) => row.key === initialKey);
    setLines(
      picked
        ? [
            {
              key: picked.key,
              qty: "",
              condition: "opened",
              opened: new Date().toISOString().slice(0, 10),
            },
          ]
        : [],
    );
    setSearch("");
    setReason(picked?.expired > 0 ? "expired" : "correction");
    setRemarks("");
    // rows change after every save, the picked item is read when opening
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, initialKey]);

  const rowOf = (key) => rows.find((row) => row.key === key);

  // available items not yet picked, matching every word typed
  const matches = useMemo(() => {
    const picked = new Set(lines.map(({ key }) => key));
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return rows
      .filter(({ key }) => !picked.has(key))
      .filter(({ name, generic, lots }) => {
        const text = [
          generic?.abbreviation,
          generic?.name,
          name,
          ...lots.map(({ lot }) => lot),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return words.every((word) => text.includes(word));
      });
  }, [rows, lines, search]);

  useEffect(() => setCursor(0), [search]);

  const pick = (row) => {
    if (!row) return;
    // opened today unless changed
    setLines((prev) => [
      ...prev,
      {
        key: row.key,
        qty: "",
        condition: "opened",
        opened: new Date().toISOString().slice(0, 10),
      },
    ]);
    // an expired stock is most likely dispensed as expired
    if (!lines.length && row.expired > 0) setReason("expired");
    setSearch("");
    setIsOpen(true);
    searchRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIsOpen(true);
      setCursor((prev) => Math.min(prev + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      pick(matches[cursor]);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const update = (index, changes) =>
    setLines((prev) =>
      prev.map((line, i) => (i === index ? { ...line, ...changes } : line)),
    );

  const invalid = lines.filter(({ key, qty }) => {
    const value = Number(qty);
    return (
      !(value >= 1) || !Number.isInteger(value) || value > rowOf(key).onHand
    );
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      reason,
      remarks: remarks.trim() || undefined,
      items: lines.map(({ key, qty, condition, opened }) => {
        const row = rowOf(key);
        return {
          product: row.product._id,
          variant: row.variant?._id,
          qty: Number(qty),
          condition,
          opened: condition === "opened" ? opened || undefined : undefined,
        };
      }),
    });
  };

  return (
    <MDBModal isOpen={show} toggle={toggle} backdrop size="xl">
      <MDBModalHeader
        toggle={toggle}
        className="light-blue darken-3 white-text"
      >
        <MDBIcon icon="dolly" className="mr-2" />
        Dispense · Monthly Expense
      </MDBModalHeader>
      <form onSubmit={handleSubmit}>
        <MDBModalBody>
          {/* search to add */}
          <div className="position-relative mb-3">
            <MDBIcon
              icon="search"
              className="position-absolute text-muted"
              style={{ top: 11, left: 12 }}
            />
            <input
              ref={searchRef}
              className="form-control"
              style={{ paddingLeft: 36 }}
              placeholder="Search item or lot to dispense (e.g. syringe 3cc)"
              value={search}
              disabled={!rows.length}
              onChange={({ target }) => {
                setSearch(target.value);
                setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
              // delayed so a click on an option is not lost
              onBlur={() => setTimeout(() => setIsOpen(false), 150)}
              onKeyDown={handleKeyDown}
              autoFocus
            />
            {isOpen && (
              <div
                className="position-absolute w-100 bg-white border rounded z-depth-1"
                style={{ zIndex: 10, maxHeight: 260, overflowY: "auto" }}
              >
                {!matches.length && (
                  <div className="px-3 py-2 small text-muted">
                    {rows.length && lines.length === rows.length
                      ? "Every available item is already picked."
                      : "No available item matches."}
                  </div>
                )}
                {matches.map((row, index) => (
                  <div
                    key={row.key}
                    role="option"
                    aria-selected={index === cursor}
                    className={`px-3 py-1 d-flex align-items-center ${
                      index === cursor ? "blue lighten-5" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                    onMouseEnter={() => setCursor(index)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pick(row);
                    }}
                  >
                    {row.generic && (
                      <code className="mr-2">{row.generic.abbreviation}</code>
                    )}
                    <span className="text-truncate mr-2">
                      <b>{row.name}</b>
                      <small className="text-muted ml-2">
                        {row.generic?.name}
                      </small>
                    </span>
                    <small
                      className={`ml-auto text-nowrap ${
                        row.expired > 0 ? "text-danger" : "text-muted"
                      }`}
                    >
                      {row.onHand} {row.base}
                      {row.expired > 0 && ` · ${row.expired} expired`}
                    </small>
                  </div>
                ))}
              </div>
            )}
          </div>

          <MDBTable small className="mb-3">
            <thead className="grey lighten-4">
              <tr>
                <th>Item</th>
                <th className="text-right" style={{ width: 110 }}>
                  Available
                </th>
                <th style={{ width: 110 }}>Dispense</th>
                <th className="text-right" style={{ width: 80 }}>
                  Left
                </th>
                <th style={{ width: 150 }}>Status</th>
                <th style={{ width: 150 }}>Date opened</th>
                <th style={{ width: 30 }} />
              </tr>
            </thead>
            <tbody>
              {!lines.length && (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-3">
                    Search above to pick the items to dispense.
                  </td>
                </tr>
              )}
              {lines.map((line, index) => {
                const row = rowOf(line.key);
                if (!row) return null;
                const value = Number(line.qty) || 0;
                const tooMany = value > row.onHand;
                return (
                  <tr key={line.key}>
                    <td className="align-middle text-nowrap">
                      {row.generic && (
                        <code className="mr-2">{row.generic.abbreviation}</code>
                      )}
                      <b>{row.name}</b>
                    </td>
                    <td className="align-middle text-right small">
                      {row.onHand} {row.base}
                    </td>
                    <td className="align-middle">
                      <input
                        type="number"
                        min={1}
                        max={row.onHand}
                        step={1}
                        className={`form-control form-control-sm text-center ${
                          tooMany ? "is-invalid" : ""
                        }`}
                        value={line.qty}
                        placeholder={row.base}
                        onChange={({ target }) =>
                          update(index, { qty: target.value })
                        }
                      />
                    </td>
                    <td
                      className={`align-middle text-right small ${
                        tooMany ? "text-danger" : "text-muted"
                      }`}
                    >
                      {tooMany ? "too many" : row.onHand - value}
                    </td>
                    <td className="align-middle">
                      <div className="btn-group" role="group">
                        {[
                          ["opened", "box-open", "warning"],
                          ["intact", "box", "success"],
                        ].map(([key, icon, color]) => (
                          <button
                            key={key}
                            type="button"
                            className={`btn btn-sm m-0 px-2 py-1 z-depth-0 ${
                              line.condition === key
                                ? `btn-${color}`
                                : `btn-outline-${color}`
                            }`}
                            style={{ textTransform: "none" }}
                            title={
                              key === "opened"
                                ? "Opened / in use"
                                : "Still sealed, not opened"
                            }
                            onClick={() => update(index, { condition: key })}
                          >
                            <MDBIcon icon={icon} className="mr-1" />
                            {key === "opened" ? "Opened" : "Intact"}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td className="align-middle">
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        // an intact (sealed) stock has no opening date
                        disabled={line.condition === "intact"}
                        value={line.condition === "intact" ? "" : line.opened}
                        max={new Date().toISOString().slice(0, 10)}
                        title="When it was opened / started to be used"
                        onChange={({ target }) =>
                          update(index, { opened: target.value })
                        }
                      />
                    </td>
                    <td className="align-middle text-center">
                      <MDBIcon
                        icon="trash"
                        className="text-danger"
                        style={{ cursor: "pointer" }}
                        title="Remove"
                        onClick={() =>
                          setLines((prev) => prev.filter((_, i) => i !== index))
                        }
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </MDBTable>

          <div className="d-flex flex-wrap" style={{ gap: 12 }}>
            <div style={{ width: 240 }}>
              <label className="small text-muted mb-1 d-block">Reason</label>
              <select
                className="browser-default custom-select custom-select-sm"
                value={reason}
                onChange={({ target }) => setReason(target.value)}
              >
                {Object.entries(REASONS).map(([key, { label }]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <label className="small text-muted mb-1 d-block">Remarks</label>
              <input
                className="form-control form-control-sm"
                value={remarks}
                placeholder="e.g. monthly count, lot L2405 expired"
                onChange={({ target }) => setRemarks(target.value)}
              />
            </div>
          </div>
        </MDBModalBody>
        <MDBModalFooter className="py-1">
          <small className="text-muted mr-auto">{lines.length} item(s)</small>
          <MDBBtn size="sm" color="light" onClick={toggle}>
            Cancel
          </MDBBtn>
          <MDBBtn
            type="submit"
            size="sm"
            color="primary"
            disabled={isLoading || !lines.length || invalid.length > 0}
            title={
              invalid.length
                ? "Enter a valid quantity (not more than available) for every item"
                : undefined
            }
          >
            <MDBIcon icon="check" className="mr-1" /> Dispense
          </MDBBtn>
        </MDBModalFooter>
      </form>
    </MDBModal>
  );
}
